import { memo, useEffect, useMemo } from 'react';
import {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { runOnJS } from 'react-native-worklets';
import { MODAL_CONFIG } from './Modal.content';
import { ModalProps } from './Modal.types';
import useBreakpointProp from '../../hooks/useBreakpointProp';
import ModalOverlay from './ModalOverlay/ModalOverlay';
import ModalHeader from './ModalHeader/ModalHeader';
import Box from '../Box/Box';
import { BackgroundTokens } from '../../theme/index.types';

function Modal({
  id,
  title,
  titleNS,
  titleKey,
  hideHeader,
  variant,
  closing,
  canClose,
  children,
  style,
  close,
  onClosed,
}: ModalProps) {
  const { getAnimatedStyle, ...restConfig } = useBreakpointProp(MODAL_CONFIG);
  const animatedValue = useSharedValue(0.1);
  const animatedStyle = useAnimatedStyle(
    () => getAnimatedStyle(animatedValue),
    [getAnimatedStyle],
  );

  useEffect(() => {
    animatedValue.value = withTiming(Number(!closing), undefined, () => {
      if (closing && onClosed) {
        runOnJS(onClosed)(id);
      }
    });
  }, [id, closing, animatedValue, onClosed]);

  const backgroundColor = useMemo((): BackgroundTokens => {
    switch (variant) {
      case 'transparent':
        return 'transparent';
      case 'default':
      default:
        return 'screenBackground';
    }
  }, [variant]);

  return (
    <Box absoluteFill centerContent>
      <ModalOverlay onPress={canClose ? close : undefined} />
      <Box
        pointerEvents="auto"
        backgroundColor={backgroundColor}
        animatedStyle={animatedStyle}
        {...restConfig}
        {...style}
      >
        {!hideHeader && (
          <ModalHeader
            title={title}
            titleNS={titleNS}
            titleKey={titleKey}
            canClose={canClose}
            close={close}
          />
        )}
        {children}
      </Box>
    </Box>
  );
}

export default memo(Modal);
